import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Section from '../components/common/Section';
import Button from '../components/common/Button';
import { Plus, Minus, MessageCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const faqTabs = ['All', 'Courses', 'Payments', 'Placement', 'Technical'];

const faqData = [
    {
        category: 'Courses',
        q: 'Do I need any prior experience to join the course?',
        a: 'No prior experience is required. Our courses are designed for beginners and cover everything from the fundamentals to advanced concepts.',
    },
    {
        category: 'Courses',
        q: 'How long is the course?',
        a: 'The UI/UX Design course is 12 weeks long. Each week includes 4 live sessions, hands-on assignments, and a weekly project.',
    },
    {
        category: 'Courses',
        q: 'Is the certificate valid internationally?',
        a: 'Yes, our certificate is recognized by industry partners worldwide and can be shared on LinkedIn and your portfolio.',
    },
    {
        category: 'Courses',
        q: 'Are the sessions live or recorded?',
        a: 'All sessions are live and interactive. Recordings are available within 24 hours for revision and catch-up.',
    },
    {
        category: 'Payments',
        q: 'Do you offer EMI options?',
        a: 'Yes, we offer no-cost EMI options through our banking partners. You can split the fee into 3, 6, or 12 monthly installments.',
    },
    {
        category: 'Payments',
        q: 'What is your refund policy?',
        a: 'We offer a full refund within the first 7 days if you are not satisfied with the course. After 7 days, a prorated refund is available.',
    },
    {
        category: 'Payments',
        q: 'Are there any group discounts?',
        a: 'Yes! Groups of 3+ get a 10% discount, and groups of 5+ get a 15% discount. Contact us for custom team pricing.',
    },
    {
        category: 'Payments',
        q: 'What payment methods do you accept?',
        a: 'We accept UPI, credit/debit cards, net banking, and bank transfers. International students can pay via PayPal or Stripe.',
    },
    {
        category: 'Placement',
        q: 'Do you guarantee a job after the course?',
        a: 'We offer a placement assistance program with dedicated career coaching. While we cannot guarantee employment, 85% of our graduates receive job offers within 3 months.',
    },
    {
        category: 'Placement',
        q: 'Do you help with resume building?',
        a: 'Yes, you get personalized resume reviews and a professional template. We also help optimize your LinkedIn profile.',
    },
    {
        category: 'Placement',
        q: 'How does interview preparation work?',
        a: 'We conduct mock interviews with industry professionals, provide common question banks, and teach you how to present your portfolio effectively.',
    },
    {
        category: 'Placement',
        q: 'Do you review my portfolio?',
        a: 'Yes, our mentors provide detailed feedback on your portfolio projects and help you present them professionally to potential employers.',
    },
    {
        category: 'Technical',
        q: 'What kind of laptop do I need?',
        a: 'Any modern laptop (2019 or newer) with 8GB+ RAM works fine. We provide a detailed hardware guide after enrollment.',
    },
    {
        category: 'Technical',
        q: 'What software is required?',
        a: 'You will need Figma (free version), VS Code, and a modern browser. All tools we use have free plans for students.',
    },
    {
        category: 'Technical',
        q: 'What internet speed is needed?',
        a: 'A minimum of 5 Mbps is recommended for smooth live sessions. Recordings can be downloaded on slower connections.',
    },
    {
        category: 'Technical',
        q: 'Will I get access to the code repository?',
        a: 'Yes, you get lifetime access to our private GitHub repositories with all course projects, starter code, and bonus resources.',
    },
];

const CourseFAQ = () => {
    const [activeTab, setActiveTab] = useState('All');
    const [openIndex, setOpenIndex] = useState(null);

    const filteredFAQs = activeTab === 'All'
        ? faqData
        : faqData.filter((faq) => faq.category === activeTab);

    const toggleFAQ = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <>
            <Helmet>
                <title>Course FAQ | PyrusMedia</title>
                <meta name="description" content="Get answers to common questions about our courses, payments, placement support, and technical requirements." />
            </Helmet>

            {/* Hero */}
            <section className="pt-32 pb-20 bg-white text-center relative overflow-hidden">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#bebd19]/20 rounded-full blur-[100px] pointer-events-none" />
                <div className="container mx-auto px-4 relative z-10">
                    <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h1>
                    <p className="text-xl text-gray-500 max-w-3xl mx-auto">
                        Everything you need to know about our courses, payments, and placement.
                    </p>
                </div>
            </section>

            {/* Tab Filter */}
            <Section className="bg-white pt-8 pb-4">
                <div className="flex flex-wrap justify-center gap-3">
                    {faqTabs.map((tab) => (
                        <button
                            key={tab}
                            onClick={() => {
                                setActiveTab(tab);
                                setOpenIndex(null);
                            }}
                            className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 border ${
                                activeTab === tab
                                    ? 'bg-[#bebd19] text-gray-900 border-[#bebd19]'
                                    : 'bg-gray-100 text-gray-500 border-gray-200 hover:border-[#bebd19] hover:text-gray-900'
                            }`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>
            </Section>

            {/* FAQ Accordion */}
            <Section className="bg-white min-h-[60vh] py-16">
                <div className="max-w-3xl mx-auto space-y-4">
                    {filteredFAQs.map((faq, index) => (
                        <motion.div
                            key={`${faq.category}-${index}`}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.3, delay: index * 0.05 }}
                            className={`bg-gray-100 rounded-xl border transition-colors ${
                                openIndex === index ? 'border-[#bebd19]' : 'border-gray-200'
                            }`}
                        >
                            <button
                                className="w-full p-6 flex items-center justify-between text-left"
                                onClick={() => toggleFAQ(index)}
                            >
                                <div className="flex-1">
                                    <span className="text-xs font-bold text-[#bebd19] uppercase tracking-wider mr-3">
                                        {faq.category}
                                    </span>
                                    <span className="text-lg font-semibold text-gray-900">{faq.q}</span>
                                </div>
                                {openIndex === index ? (
                                    <Minus className="text-[#bebd19] shrink-0 ml-4" />
                                ) : (
                                    <Plus className="text-gray-500 shrink-0 ml-4" />
                                )}
                            </button>
                            <AnimatePresence>
                                {openIndex === index && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        className="overflow-hidden"
                                    >
                                        <div className="px-6 pb-6 text-gray-500 leading-relaxed border-t border-gray-200 pt-4">
                                            {faq.a}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    ))}
                </div>

                {/* CTA */}
                <div className="max-w-2xl mx-auto mt-20 text-center bg-gray-100 rounded-2xl border border-gray-200 p-10">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Still have questions?</h3>
                    <p className="text-gray-500 mb-8">
                        We are here to help. Reach out and we will get back to you within 24 hours.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Button to="/contact">Contact Us</Button>
                        <a
                            href="https://wa.me/919999999999"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline inline-flex items-center gap-2"
                        >
                            <MessageCircle size={18} /> Chat on WhatsApp
                        </a>
                    </div>
                </div>
            </Section>
        </>
    );
};

export default CourseFAQ;
